<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import {
    AppEmptyState,
    AppLoadingState,
    AppErrorState,
    AppToolbar,
    SortControl,
    StatusPills,
    AssessmentTile,
    AssessmentPager,
    relativeTime,
    matchesSearch,
    paginate,
  } from '../../framework/components/app';
  import type { RingSpec, RingTone, StatusTone, PillOption } from '../../framework/components/app';

  interface AssessmentRow {
    uuid: string;
    title: string | null;
    display_ref: string | null;
    verdict?: string | null;
    verdict_label: string | null;
    generation_status: string;
    updated_at: string;
    created_at: string;
  }

  let items: AssessmentRow[] = [];
  let state: 'loading' | 'ready' | 'error' = 'loading';
  let searchQuery = '';
  let statusFilter: string[] = [];
  let sortField: 'newest' | 'oldest' | 'titleAsc' | 'titleDesc' = 'newest';
  let page = 1;
  let pageSize = 20;

  const STATUS_OPTIONS: PillOption[] = [
    { value: 'decided', label: 'Decided' },
    { value: 'draft', label: 'Draft' },
  ];

  /** Aligned with tile “Decided” (verdict or completed). */
  function isDecidedRow(row: AssessmentRow): boolean {
    if (row.verdict_label && String(row.verdict_label).trim() !== '') return true;
    return (row.generation_status || '').toLowerCase() === 'completed';
  }

  /** Aligned with tile “Draft” — excludes in-flight and failed. */
  function isDraftRow(row: AssessmentRow): boolean {
    if (isDecidedRow(row)) return false;
    const gs = (row.generation_status || '').toLowerCase();
    return !['processing', 'queued', 'failed'].includes(gs);
  }

  const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'titleAsc', label: 'A – Z' },
    { value: 'titleDesc', label: 'Z – A' },
  ];

  async function loadData() {
    state = 'loading';
    try {
      const res: any = await api.getAssessments('', 'updated', 'desc');
      if (res?.success && res?.data?.list) {
        items = res.data.list;
        state = 'ready';
      } else {
        state = 'error';
      }
    } catch {
      state = 'error';
    }
  }

  function onSearch(e: CustomEvent<string>) {
    searchQuery = e.detail;
    page = 1;
  }

  function onStatusChange(e: CustomEvent<string[]>) {
    statusFilter = e.detail;
    page = 1;
  }

  function onSort(e: CustomEvent<{ field: string }>) {
    sortField = e.detail.field as typeof sortField;
    page = 1;
  }

  /**
   * Interpret the verdict label into a canonical yes/no/maybe.
   * Order matters: "Not Needed" / "Not Recommended" are NO, not YES.
   */
  function verdictInterpret(label: string, verdict?: string | null): 'yes' | 'no' | 'maybe' {
    if (verdict === 'not_recommended') return 'no';
    if (verdict === 'recommended') return 'yes';
    if (verdict === 'conditional') return 'maybe';

    const l = (label || '').toLowerCase().trim();
    if (!l) return 'maybe';
    if (/\bnot\s+recommended\b/.test(l) || /\bnot\s+needed\b/.test(l)) return 'no';
    if (l.startsWith('not ') || /\b(skip|don.?t|unnecessary)\b/.test(l) || l === 'no') return 'no';
    if (/\b(optional|conditional|consider|perhaps|possibly)\b/.test(l)) return 'maybe';
    if (/\b(recommended|needed|required)\b/.test(l) && !/\bnot\b/.test(l)) return 'yes';
    return 'maybe';
  }

  function verdictWord(label: string, verdict?: string | null): string {
    const v = verdictInterpret(label, verdict);
    if (v === 'no') return 'NO';
    if (v === 'maybe') return 'MAYBE';
    return 'YES';
  }

  function verdictRingTone(label: string, verdict?: string | null): RingTone {
    const v = verdictInterpret(label, verdict);
    if (v === 'no') return 'success';
    if (v === 'maybe') return 'warn';
    return 'info';
  }

  function verdictBadgeTone(label: string, verdict?: string | null): StatusTone {
    const v = verdictInterpret(label, verdict);
    if (v === 'no') return 'success';
    if (v === 'maybe') return 'warn';
    return 'info';
  }

  function tileFor(row: AssessmentRow): {
    ring: RingSpec;
    ringTone: RingTone;
    statusLabel: string;
    statusTone: StatusTone;
    statusDetail: string;
    footerNote: string;
  } {
    const gs = (row.generation_status || '').toLowerCase();
    if (row.verdict_label) {
      return {
        ring: { kind: 'word', text: verdictWord(row.verdict_label, row.verdict) },
        ringTone: verdictRingTone(row.verdict_label, row.verdict),
        statusLabel: 'Decided',
        statusTone: verdictBadgeTone(row.verdict_label, row.verdict),
        statusDetail: row.verdict_label,
        footerNote: `Decided ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    if (gs === 'processing') {
      return {
        ring: { kind: 'dots' },
        ringTone: 'progress',
        statusLabel: 'Reasoning',
        statusTone: 'info',
        statusDetail: '',
        footerNote: `Started ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    if (gs === 'queued') {
      return {
        ring: { kind: 'dots' },
        ringTone: 'neutral',
        statusLabel: 'Queued',
        statusTone: 'neutral',
        statusDetail: '',
        footerNote: `Submitted ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    if (gs === 'failed') {
      return {
        ring: { kind: 'icon', name: 'exclamation' },
        ringTone: 'danger',
        statusLabel: 'Failed',
        statusTone: 'danger',
        statusDetail: 'Retry available',
        footerNote: `Failed ${relativeTime(row.updated_at || row.created_at)}`,
      };
    }
    return {
      ring: { kind: 'icon', name: 'pencil' },
      ringTone: 'neutral',
      statusLabel: 'Draft',
      statusTone: 'neutral',
      statusDetail: '',
      footerNote: `Edited ${relativeTime(row.updated_at || row.created_at)}`,
    };
  }

  function compareItems(a: AssessmentRow, b: AssessmentRow, field: typeof sortField): number {
    if (field === 'newest') {
      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    }
    if (field === 'oldest') {
      return new Date(a.updated_at || a.created_at).getTime() - new Date(b.updated_at || b.created_at).getTime();
    }
    if (field === 'titleDesc') {
      return (b.title || '').localeCompare(a.title || '');
    }
    return (a.title || '').localeCompare(b.title || '');
  }

  $: filtered = items.filter((row) => {
    if (statusFilter.length > 0) {
      const f = statusFilter[0];
      if (f === 'decided' && !isDecidedRow(row)) return false;
      if (f === 'draft' && !isDraftRow(row)) return false;
    }
    if (searchQuery.length >= 3) {
      return matchesSearch(row.title || '', searchQuery)
        || matchesSearch(row.display_ref || '', searchQuery);
    }
    return true;
  });

  $: sorted = [...filtered].sort((a, b) => compareItems(a, b, sortField));
  $: totalCount = sorted.length;
  $: pagedItems = paginate(sorted, page, pageSize);

  $: statusCounts = STATUS_OPTIONS.map((o) => ({
    ...o,
    count: items.filter((r) => (o.value === 'decided' ? isDecidedRow(r) : isDraftRow(r))).length,
  }));

  onMount(loadData);
</script>

{#if state === 'loading'}
  <AppLoadingState label="Loading assessments" />
{:else if state === 'error'}
  <AppErrorState on:retry={loadData} />
{:else if items.length === 0}
  <AppEmptyState
    icon="clipboard list"
    title="No assessments yet"
    description="Start your first assessment to see results here."
    actionLabel="New assessment"
    actionHref="/assessments/new"
  />
{:else}
  <div class="filter-bar">
    <AppToolbar
      bind:searchValue={searchQuery}
      searchPlaceholder="Search by title or REF…"
      searchLabel="Search assessments"
      resultsCount={totalCount}
      resultsSingular="assessment"
      resultsPlural="assessments"
      on:search={onSearch}
    >
      <SortControl
        slot="controls"
        options={SORT_OPTIONS}
        field={sortField}
        on:change={onSort}
      />
    </AppToolbar>
    <StatusPills
      options={statusCounts}
      selected={statusFilter}
      on:change={onStatusChange}
    />
  </div>

  {#if pagedItems.length === 0}
    <div class="empty-filter">
      <p>No assessments match your filters.</p>
      <button type="button" class="reset-btn" on:click={() => { searchQuery = ''; statusFilter = []; page = 1; }}>
        Clear filters
      </button>
    </div>
  {:else}
    <div class="tile-grid">
      {#each pagedItems as row (row.uuid)}
        {@const t = tileFor(row)}
        <AssessmentTile
          agent="illuminator"
          href={`/assessments/${row.uuid}`}
          title={row.title || 'Untitled'}
          ring={t.ring}
          ringTone={t.ringTone}
          statusLabel={t.statusLabel}
          statusTone={t.statusTone}
          statusDetail={t.statusDetail}
          refId={row.display_ref ? `#${row.display_ref}` : ''}
          footerNote={t.footerNote}
          resumeHref={isDraftRow(row) ? `/assessments/${row.uuid}/edit` : ''}
        />
      {/each}
    </div>
  {/if}

  <AssessmentPager
    bind:page
    bind:pageSize
    totalCount={totalCount}
  />
{/if}

<style>
  .filter-bar {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-5);
  }

  .tile-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-6);
  }

  @media (max-width: 1440px) {
    .tile-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }

  @media (max-width: 1024px) {
    .tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @media (max-width: 640px) {
    .tile-grid { grid-template-columns: 1fr; }
  }

  .empty-filter {
    text-align: center;
    padding: var(--space-8) var(--space-4);
    color: var(--grey-secondary);
  }

  .empty-filter p { margin: 0 0 var(--space-3) 0; }

  .reset-btn {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    color: var(--primary);
    font: inherit;
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
  }

  .reset-btn:hover { background: var(--surface-hover); border-color: var(--primary); }
</style>
